import type { components, StreamedCitation } from "@soulbyte/core";
import { useHasRole } from "@/lib/auth/auth.roles";
import AdaptiveDialog, {
  AdaptiveDialogClose,
  AdaptiveDialogContent,
  AdaptiveDialogHeader,
  AdaptiveDialogTitle,
  AdaptiveDialogTrigger,
} from "@/lib/components/ui/adaptive-dialog/adaptive-dialog";
import Badge from "@/lib/components/ui/badge/badge";
import Check from "@/lib/components/ui/icons/check";
import File from "@/lib/components/ui/icons/file";
import Shield from "@/lib/components/ui/icons/shield";
import Text from "@/lib/components/ui/text/text";
import { cn } from "@/lib/utils";
import { fromKnowledgeUploadFileName } from "@/modules/assistants/knowledge/knowledge-sources.paths";
import { stripAssetSuffix } from "@/modules/chat/attachments/attachments.utils";
import { Waypoints } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

export type CitationRelation = {
  subject: string;
  relation: string;
  object: string;
};

export type Citation = StreamedCitation & {
  sourceType?: string;
  relation?: CitationRelation;
};

type RAGChunk = components["schemas"]["RAGChunk"];

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/**
 * piiTokenNames recovers the readable value behind each `{PERSON_…}` token by
 * aligning the encoded graph text with the decoded excerpt, which is the same
 * sentence with the tokens replaced by names.
 */
export function piiTokenNames(
  encodedText: string,
  decodedText: string,
): Map<string, string> {
  const tokens = encodedText.match(/\{[^}]+\}/g);
  if (!tokens) return new Map();
  const pattern = new RegExp(
    `^${encodedText
      .split(/\{[^}]+\}/)
      .map(escapeRegExp)
      .join("(.+?)")}$`,
  );
  const match = decodedText.trim().match(pattern);
  if (!match) return new Map();
  return new Map(tokens.map((token, i) => [token, match[i + 1].trim()]));
}

/**
 * relationFromMetadata reads the graph triple a graph-rag chunk carries in its
 * metadata. Token values fall back to the bare token name when the excerpt
 * alignment cannot decode them.
 */
export function relationFromMetadata(
  metadata: Record<string, unknown> | null | undefined,
  excerpt: string,
): CitationRelation | undefined {
  if (!metadata) return undefined;
  const { subject, relation, object, text } = metadata;
  if (
    typeof subject !== "string" ||
    typeof relation !== "string" ||
    typeof object !== "string"
  ) {
    return undefined;
  }
  const names =
    typeof text === "string"
      ? piiTokenNames(text, excerpt)
      : new Map<string, string>();
  const decode = (value: string) =>
    names.get(value) ?? value.replace(/^\{|\}$/g, "");
  return { subject: decode(subject), relation, object: decode(object) };
}

/**
 * citationTitle resolves `__sb_relpath__`-encoded upload names back to the
 * readable path, e.g. `02_fachbereich/20001_geschäftsführung.pdf`. The folders
 * stay — they say where the source sits — but the backend's `_asset` marker
 * drops off the file name itself.
 */
export function citationTitle(title: string): string {
  if (!title.trim()) return title;
  const decoded = fromKnowledgeUploadFileName(title);
  const folders = (decoded.relativePath ?? decoded.name).split("/");
  const fileName = stripAssetSuffix(folders.pop() ?? "");
  return [...folders, fileName].join("/");
}

/**
 * UI badge expects {@link StreamedCitation}; API / WS may send {@link RAGChunk} instead.
 */
export function citationsForBadge(
  citations: (StreamedCitation | RAGChunk)[] | undefined | null,
): Citation[] | null {
  if (!citations?.length) return null;
  const mapped = citations.map((c): Citation => {
    if (
      "source" in c &&
      typeof (c as StreamedCitation).source === "string" &&
      "score" in c &&
      typeof (c as StreamedCitation).score === "number"
    ) {
      const s = c as StreamedCitation;
      return {
        id: s.id,
        title: citationTitle(s.title),
        excerpt: s.excerpt,
        source: s.source,
        score: s.score,
        sourceType: s.source_type ?? undefined,
        relation: relationFromMetadata(s.metadata, s.excerpt),
      };
    }
    const chunk = c as RAGChunk;
    return {
      id: chunk.id ?? "",
      title: citationTitle(chunk.title ?? ""),
      excerpt: chunk.excerpt ?? "",
      source: chunk.file_url ?? chunk.document_id ?? "",
      score: chunk.relevance ?? 0,
      sourceType: chunk.source_type,
      relation: relationFromMetadata(chunk.metadata, chunk.excerpt ?? ""),
    };
  });

  // reloaded messages carry one row per streamed delta, so the same chunk
  // shows up many times; keep the highest-scored copy of each
  const unique = new Map<string, Citation>();
  for (const citation of mapped) {
    const key = `${citation.title}|${citation.excerpt.trim()}|${citation.source}`;
    const existing = unique.get(key);
    if (!existing || citation.score > existing.score) {
      unique.set(key, citation);
    }
  }
  return [...unique.values()];
}

function getRelevanceMeta(score?: number) {
  const percent = Math.round((score ?? 0) * 100);
  if (percent >= 90) {
    return {
      label: "Very Relevant",
      percent,
      bg: "bg-[#042F0B]", // dark green
      iconBg: "bg-[#34C759]", // lighter green
      text: "text-[#00E022]",
      icon: "check" as const,
    };
  } else {
    return {
      label: "Relevant",
      percent,
      bg: "bg-[#1E2A5C]", // dark blue
      iconBg: "bg-[#428AF8]", // lighter blue
      text: "text-[#9BC5F6]",
      icon: "shield" as const,
    };
  }
}

function SourceTypeLabel({ sourceType }: { sourceType: string }) {
  const { t } = useTranslation();
  const isGraph = sourceType.toLowerCase().includes("graph");
  return (
    <span className="text-foreground inline-flex items-center gap-2">
      <span className="bg-background-accent flex size-6 shrink-0 items-center justify-center rounded-full">
        {isGraph ? (
          <Waypoints className="size-3.5" />
        ) : (
          <File className="size-3.5" />
        )}
      </span>
      <Text variant="title" weight="semibold">
        {isGraph
          ? t("chat_citations_source_type_graph")
          : t("chat_citations_source_type_rag")}
      </Text>
    </span>
  );
}

function RelationNodeCircle({ label }: { label: string }) {
  return (
    <span
      className="bg-background-accent text-foreground border-primary flex size-20 shrink-0 items-center justify-center overflow-hidden rounded-full border-2 p-2 text-center text-xs"
      title={label}
    >
      <span className="line-clamp-3 break-words">{label}</span>
    </span>
  );
}

function RelationGraph({ relation }: { relation: CitationRelation }) {
  return (
    <div className="flex items-center justify-center py-3">
      <RelationNodeCircle label={relation.subject} />
      <div className="relative mx-1 flex w-32 items-center">
        <span className="absolute -top-5 left-1/2 -translate-x-1/2 text-xs whitespace-nowrap text-foreground-pressed">
          {relation.relation.replaceAll("_", " ")}
        </span>
        <span className="h-px flex-1 bg-border" />
        <span
          className="border-y-[4px] border-l-[6px] border-y-transparent border-l-border"
          aria-hidden
        />
      </div>
      <RelationNodeCircle label={relation.object} />
    </div>
  );
}

function SourcesGlyph({ count }: { count: number }) {
  const visible = Math.max(1, Math.min(count, 3));
  const items = Array.from({ length: visible });
  const extra = Math.max(0, count - visible);

  return (
    <span className="relative inline-flex shrink-0 items-center">
      {items.map((_, idx) => (
        <span
          key={idx}
          className={cn(
            "bg-background-accent text-foreground-on-accent flex size-6 items-center justify-center rounded-full",
            idx > 0 && "-ml-3",
          )}
          aria-hidden
        >
          <File className="size-3.5" />
        </span>
      ))}
      {extra > 0 && (
        <span className="bg-background text-foreground ring-background -ml-2 flex size-5 items-center justify-center rounded-full text-[10px] font-semibold ring-2">
          +{extra}
        </span>
      )}
    </span>
  );
}

export function CitationsBadge({
  citations,
}: {
  citations?: Citation[] | null;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const showRelevanceScores = useHasRole("SuperAdmin");

  if (!citations || citations.length === 0) return null;

  const sourcesLabel = t("chat_citations_sources", { count: citations.length });

  return (
    <AdaptiveDialog open={open} onOpenChange={setOpen}>
      <AdaptiveDialogTrigger asChild>
        <Badge
          size="lg"
          radius="full"
          variant="secondary"
          asChild
          aria-label={sourcesLabel}
          className="text-foreground-hovered data-interactive:hover:bg-background-neutral-hovered data-interactive:hover:text-foreground bg-transparent"
        >
          <button>
            <span className="truncate">{sourcesLabel}</span>
          </button>
        </Badge>
      </AdaptiveDialogTrigger>
      <AdaptiveDialogContent
        width="max-w-[min(90vw,1100px)]"
        className="max-h-[70vh] outline-none focus-visible:ring-0 focus-visible:outline-none sm:max-h-[75vh]"
      >
        <AdaptiveDialogClose />
        <AdaptiveDialogHeader>
          <AdaptiveDialogTitle className="flex items-center justify-center gap-2 text-center">
            <SourcesGlyph count={citations.length} />
            {sourcesLabel}
          </AdaptiveDialogTitle>
        </AdaptiveDialogHeader>
        <div className="flex flex-col gap-4">
          {citations
            .slice()
            .sort((a, b) => (b.score ?? 0) - (a.score ?? 0))
            .map((item) => {
              const meta = getRelevanceMeta(item.score);
              const href = item.source;
              const isExternalLink = /^https?:\/\//.test(href);
              return (
                <div
                  key={item.id}
                  className="text-foreground flex flex-col gap-3 p-4"
                >
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="flex w-full flex-row justify-between">
                      {item.sourceType ? (
                        <SourceTypeLabel sourceType={item.sourceType} />
                      ) : (
                        <Text variant="title" weight="semibold">
                          {item.title || "Untitled source"}
                        </Text>
                      )}
                      <span
                        className={cn(
                          "inline-flex items-center gap-1.5 rounded-full px-2 py-1 text-sm leading-none font-medium",
                          meta.bg,
                          meta.text,
                        )}
                      >
                        <span
                          className={cn(
                            "flex size-4 items-center justify-center rounded-full",
                            meta.iconBg,
                          )}
                        >
                          {meta.icon === "check" ? (
                            <Check className="size-3 text-white" />
                          ) : (
                            <Shield className="size-3 text-white" />
                          )}
                        </span>
                        {meta.label}
                        {showRelevanceScores && ` — ${meta.percent}%`}
                      </span>
                    </div>
                  </div>
                  {item.relation && <RelationGraph relation={item.relation} />}
                  {item.excerpt && (
                    <div className="flex gap-3">
                      <span className="mt-0.5 h-full min-h-10 w-[4px] min-w-[4px] bg-[#575757]" />
                      <Text className="my-2 text-white" variant="label">
                        {item.excerpt}
                      </Text>
                    </div>
                  )}
                  {isExternalLink ? (
                    <a
                      className={cn(
                        "text-primary mt-1 ml-1 inline-flex w-fit items-center gap-2 rounded-full px-2 py-1 text-sm leading-5 font-normal tracking-[0.0025em] underline-offset-2",
                        "hover:bg-background-neutral-hovered",
                      )}
                      href={href}
                      target="_blank"
                      rel="noreferrer"
                    >
                      <span className="bg-background-accent text-foreground flex size-6 items-center justify-center rounded-full">
                        <File className="size-3.5" />
                      </span>
                      Open: {item.title || "source"}
                    </a>
                  ) : (
                    <Text
                      className="text-primary mt-1 ml-1 text-sm"
                      variant="label"
                    >
                      Source: {item.source}
                    </Text>
                  )}
                </div>
              );
            })}
        </div>
      </AdaptiveDialogContent>
    </AdaptiveDialog>
  );
}
